<template>
  <div v-if="isSuperadminMode" class="superadmin-banner superadmin-banner-bottom">
    <!-- Collapsed State - Thin bar -->
    <div v-if="isCollapsed" class="superadmin-collapsed" @click="toggleCollapsed">
      <div class="d-flex align-items-center justify-content-center">
        <feather-icon icon="AlertTriangleIcon" size="12" class="mr-1" />
        <small class="mr-2"><strong>SUPERADMIN</strong></small>
        <small class="mr-2">{{ formattedSessionTime }}</small>
        <feather-icon icon="ChevronUpIcon" size="14" />
      </div>
    </div>

    <!-- Expanded State - Full banner -->
    <template v-else>
      <!-- Superadmin Warning Banner -->
      <div class="superadmin-alert mb-0 py-1 px-2">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <feather-icon icon="AlertTriangleIcon" size="14" class="mr-1" />
            <strong style="font-size: 0.8rem">SUPERADMIN MODE</strong>
            <span class="mx-1">|</span>
            <span style="font-size: 0.75rem">
              <template v-if="isImpersonating">
                Acting as owner of <strong>{{ currentTenantName }}</strong>
              </template>
              <template v-else>
                {{ currentTenantName }}
              </template>
            </span>
            <span class="mx-1">|</span>
            <span style="font-size: 0.75rem">{{ formattedSessionTime }}</span>
          </div>

          <div class="d-flex align-items-center">
            <!-- Quick Tenant Switcher -->
            <b-dropdown
              variant="outline-primary"
              size="sm"
              class="mr-1 tenant-dropdown"
              :text="currentTenantName"
              no-caret
              menu-class="tenant-dropdown-menu"
            >
              <template #button-content>
                <span style="font-size: 0.7rem">{{ currentTenantName }}</span>
                <feather-icon icon="ChevronDownIcon" size="12" class="ml-25" />
              </template>

              <b-dropdown-item class="tenant-dd-item" @click="handleTenantSwitch(null)">
                <div class="d-flex align-items-center">
                  <feather-icon icon="GlobeIcon" size="12" class="mr-50 text-primary" />
                  <span class="tenant-dd-name">All Tenants</span>
                </div>
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item
                v-for="tenant in availableTenants.slice(0, 10)"
                :key="tenant.id"
                class="tenant-dd-item"
                @click="handleTenantSwitch(tenant)"
              >
                <div class="d-flex align-items-center">
                  <feather-icon icon="BriefcaseIcon" size="12" class="mr-50 text-muted" />
                  <span class="tenant-dd-name text-truncate">{{ tenant.name }}</span>
                  <span class="tenant-dd-meta ml-auto pl-1 text-muted text-truncate">
                    {{ tenant.code }} · {{ tenant.jenis_faskes }}
                  </span>
                </div>
              </b-dropdown-item>
              <b-dropdown-divider v-if="availableTenants.length > 10" />
              <b-dropdown-item v-if="availableTenants.length > 10" class="tenant-dd-item" @click="showTenantSwitcher">
                <div class="d-flex align-items-center">
                  <feather-icon icon="SearchIcon" size="12" class="mr-50 text-primary" />
                  <span class="tenant-dd-name">View All Tenants…</span>
                </div>
              </b-dropdown-item>
            </b-dropdown>

            <!-- Workspace Admin -->
            <b-button
              v-b-tooltip.hover.top="'Workspace Admin Panel'"
              variant="outline-primary"
              size="sm"
              class="mr-1 admin-btn btn-xs"
              :disabled="loadingWorkspaceAdmin"
              @click="goToWorkspaceAdmin"
            >
              <b-spinner v-if="loadingWorkspaceAdmin" small class="mr-25" />
              <feather-icon v-else icon="ShieldIcon" size="12" />
            </b-button>

            <!-- View Audit Log -->
            <b-button
              v-b-tooltip.hover.top="'View Audit Log'"
              variant="outline-primary"
              size="sm"
              class="mr-1 admin-btn btn-xs"
              :disabled="loadingAuditLog"
              @click="showAuditLog"
            >
              <b-spinner v-if="loadingAuditLog" small class="mr-25" />
              <feather-icon v-else icon="FileTextIcon" size="12" />
            </b-button>

            <!-- Exit Superadmin -->
            <b-button
              v-b-tooltip.hover.top="'Exit Superadmin Mode'"
              variant="outline-primary"
              size="sm"
              class="mr-1 admin-btn btn-xs"
              :disabled="loadingExit"
              @click="confirmExit"
            >
              <b-spinner v-if="loadingExit" small class="mr-25" />
              <feather-icon v-else icon="LogOutIcon" size="12" />
            </b-button>

            <!-- Collapse Button -->
            <b-button
              v-b-tooltip.hover.top="'Minimize banner'"
              variant="outline-light"
              size="sm"
              class="collapse-btn btn-xs"
              @click="toggleCollapsed"
            >
              <feather-icon icon="ChevronDownIcon" size="12" />
            </b-button>
          </div>
        </div>
      </div>

      <!-- Session Warning (when < 10 minutes left) -->
      <div
        v-if="sessionTimeRemaining < 600000"
        class="session-warning mb-0 py-50 px-2"
      >
        <div class="text-center">
          <feather-icon icon="ClockIcon" size="12" class="mr-1" />
          <small style="font-size: 0.7rem">
            <strong>Session expires in {{ formattedSessionTime }}</strong>
          </small>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'SuperadminBanner',

  data() {
    return {
      isCollapsed: false,
      loadingTenantSwitch: false,
      loadingWorkspaceAdmin: false,
      loadingAuditLog: false,
      loadingExit: false
    }
  },

  computed: {
    ...mapGetters('superadmin', [
      'isSuperadminMode',
      'currentTenantName',
      'sessionTimeRemaining',
      'formattedSessionTime'
    ]),
    ...mapState('superadmin', ['currentTenant']),

    // Ensure availableTenants is always an array
    availableTenants() {
      const tenants = this.$store.state.superadmin.availableTenants
      return Array.isArray(tenants) ? tenants : []
    },

    // True when a specific tenant is selected (vs "All Tenants" overview).
    // In this mode the superadmin is acting as that tenant's owner.
    isImpersonating() {
      return this.currentTenant && this.currentTenant.id > 0
    },
  },

  methods: {
    ...mapActions('superadmin', ['exitSuperadminMode', 'fetchAuditLogs', 'fetchAvailableTenants']),

    toggleCollapsed() {
      this.isCollapsed = !this.isCollapsed
      localStorage.setItem('superadmin_banner_collapsed', this.isCollapsed ? '1' : '0')
    },

    async handleTenantSwitch(tenant) {
      this.loadingTenantSwitch = true
      try {
        if (tenant) {
          console.log('Switching to tenant:', tenant)
          await this.$store.dispatch('superadmin/switchTenant', tenant.id)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Logged in as owner',
              text: `Acting as owner of ${tenant.name}`,
              variant: 'success'
            }
          })
        } else {
          // Switch to "all tenants" mode
          console.log('Switching to all tenants mode')
          await this.$store.dispatch('superadmin/switchTenant', null)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Viewing All Tenants',
              text: 'Access to all tenant data enabled',
              variant: 'info'
            }
          })
        }

        // Navigate to dashboard to reflect new tenant context
        if (this.$route.name !== 'dashboard') {
          this.$router.push({ name: 'dashboard' })
        } else {
          // Already on dashboard - force re-render by navigating away and back
          this.$router.replace({ name: 'dashboard', query: { _t: Date.now() } })
        }
      } catch (error) {
        console.error('Tenant switch error:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Switch Failed',
            text: error.response?.data?.message || error.message || 'Failed to switch tenant',
            variant: 'danger'
          }
        })
      } finally {
        this.loadingTenantSwitch = false
      }
    },

    goToWorkspaceAdmin() {
      if (this.$route.name !== 'workspace-admin') {
        this.loadingWorkspaceAdmin = true
        this.$router.push({ name: 'workspace-admin' }).finally(() => {
          this.loadingWorkspaceAdmin = false
        })
      }
    },

    showTenantSwitcher() {
      this.$emit('show-tenant-switcher')
    },

    async showAuditLog() {
      this.loadingAuditLog = true
      try {
        await this.fetchAuditLogs()
        this.$emit('show-audit-log')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load audit logs',
            variant: 'danger'
          }
        })
      } finally {
        this.loadingAuditLog = false
      }
    },

    confirmExit() {
      this.$bvModal.msgBoxConfirm(
        'Are you sure you want to exit superadmin mode? You will be logged out and redirected to the login page.',
        {
          title: 'Exit Superadmin Mode',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Exit',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      ).then(value => {
        if (value) {
          this.loadingExit = true
          this.exitSuperadminMode()
        }
      })
    }
  },

  async mounted() {
    // Restore collapsed state from localStorage
    const savedCollapsed = localStorage.getItem('superadmin_banner_collapsed')
    this.isCollapsed = savedCollapsed === '1'

    // Fetch tenants if not available
    if (!this.availableTenants || this.availableTenants.length === 0) {
      console.log('SuperadminBanner: Fetching tenants...')
      await this.fetchAvailableTenants()
    }
    console.log('SuperadminBanner: Available tenants:', this.availableTenants)
  }
}
</script>

<style scoped>
.superadmin-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
}

/* Collapsed state - thin bar */
.superadmin-collapsed {
  background: rgba(220, 53, 69, 0.95);
  color: white;
  padding: 4px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.superadmin-collapsed:hover {
  background: rgba(220, 53, 69, 1);
  padding: 6px 12px;
}

/* Main alert styling */
.superadmin-alert {
  background: rgba(220, 53, 69, 0.95) !important;
  border: none !important;
  color: white !important;
  border-radius: 0 !important;
}

/* Session warning styling */
.session-warning {
  background: rgba(255, 193, 7, 0.95) !important;
  border: none !important;
  color: #212529 !important;
  border-radius: 0 !important;
  animation: pulse 2s infinite;
}

/* Extra small buttons */
.btn-xs {
  padding: 0.2rem 0.4rem !important;
  font-size: 0.7rem !important;
  line-height: 1.2 !important;
}

/* Button styling */
.admin-btn {
  background: white !important;
  border-color: white !important;
  color: #dc3545 !important;
}

.admin-btn:hover {
  background: #f8f9fa !important;
  border-color: #f8f9fa !important;
  color: #c82333 !important;
}

/* Collapse button */
.collapse-btn {
  background: transparent !important;
  border-color: rgba(255,255,255,0.5) !important;
  color: white !important;
}

.collapse-btn:hover {
  background: rgba(255,255,255,0.1) !important;
  border-color: white !important;
}

/* Tenant dropdown styling */
.tenant-dropdown .btn {
  background: white !important;
  border-color: white !important;
  color: #dc3545 !important;
  padding: 0.2rem 0.5rem !important;
  font-size: 0.7rem !important;
}

.tenant-dropdown .btn:hover,
.tenant-dropdown .btn:focus,
.tenant-dropdown .btn.show {
  background: #f8f9fa !important;
  border-color: #f8f9fa !important;
  color: #c82333 !important;
  box-shadow: none !important;
}

@keyframes pulse {
  0% { opacity: 0.95; }
  50% { opacity: 0.8; }
  100% { opacity: 0.95; }
}

/* Compact tenant dropdown menu */
::v-deep .tenant-dropdown-menu {
  min-width: 280px;
  max-width: 360px;
  max-height: 320px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

::v-deep .tenant-dropdown-menu .dropdown-divider {
  margin: 0.25rem 0;
}

::v-deep .tenant-dd-item .dropdown-item {
  padding: 0.3rem 0.65rem;
  font-size: 0.72rem;
  line-height: 1.25;
}

::v-deep .tenant-dd-item .dropdown-item:hover {
  background-color: #f5f7fa;
}

::v-deep .tenant-dd-name {
  font-weight: 500;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .tenant-dd-meta {
  font-size: 0.65rem;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
